import { ComponentProps } from 'react';
import { twMerge } from 'tailwind-merge';

import { Icon } from '~/modules/elements/akui/icon';

interface Props extends Omit<ComponentProps<'div'>, 'onChange'> {
  unit?: string;
  value: number;
  onChange: (newValue: number) => void;
  step?: number;
  disabled?: boolean;
  // A stepper on a sheet sits on near-black and lifts; on the app gradient it sinks instead. Same
  // control either way, and the same height as the rows beside it in both places.
  surface?: 'sheet' | 'gradient';
}

function NumericInput({
  unit = '',
  value,
  onChange,
  step = 50,
  disabled = false,
  className,
  surface = 'gradient',
  ...props
}: Props) {
  const onSheet = surface === 'sheet';

  return (
    <div
      className={twMerge(
        'flex w-full items-center overflow-hidden rounded-[1.125rem] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)]',
        onSheet ? 'h-16 bg-white/8' : 'h-15 bg-black/55',
        disabled && 'opacity-40',
        className,
      )}
      {...props}>
      <Step
        onClick={() => onChange(value - step)}
        disabled={disabled}
        label="Less"
        test="numeric-input-down"
        wide={onSheet}
      />
      <div
        className={twMerge(
          'min-w-0 flex-1 text-center tabular-nums',
          onSheet ? 'text-[1.1875rem]' : 'text-[1.0625rem]',
        )}>
        <strong data-test="numeric-input-value">{value}</strong>
        {unit}
      </div>
      <Step
        onClick={() => onChange(value + step)}
        disabled={disabled}
        label="More"
        test="numeric-input-up"
        wide={onSheet}
      />
    </div>
  );
}
export default NumericInput;

// Real glyphs rather than "-"/"+" text: the two characters have different advance widths and sit at
// different heights, so as text they can never line up in equal boxes.
function Step({
  onClick,
  disabled,
  label,
  test,
  wide,
}: {
  onClick: () => void;
  disabled: boolean;
  label: string;
  test: string;
  wide: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      data-test={test}
      className={twMerge(
        'flex h-full shrink-0 cursor-pointer items-center justify-center border-none bg-transparent text-white transition-colors duration-100 active:bg-white/12 disabled:opacity-50',
        wide ? 'w-16' : 'w-15',
      )}>
      <Icon icon={test.endsWith('up') ? 'ic:baseline-add' : 'ic:baseline-remove'} size={6} />
    </button>
  );
}
